import React, { useState } from "react"
import { useDispatch } from "react-redux"
import { Modal, Button, Select, notification } from "antd"
import Dropzone from "react-dropzone"
import { API, Endpoints } from "utils/api"
import { setLoader } from "store/actions"
import Strings from "utils/strings"
import { humanFileSize } from "utils/utils"
import { Icons } from "components"

const ImportModal: React.FC<{
	showImportModal: boolean
	setShowImportModal: (show: boolean) => void
	businesses: any[]
	refreshData: () => void
}> = ({ showImportModal, setShowImportModal, businesses, refreshData }) => {
	const [file, setFile] = useState<any>(null)
	const [showResponse, setShowResponse] = useState<any>(null)
	const [selectedBusiness, setSelectedBusiness] = useState<string>("")

	const handleClose = () => {
		setShowImportModal(false)
		setShowResponse(null)
		setFile(null)
		setSelectedBusiness("")
	}

	const handleShowResponse = ({
		successCount,
		failedObjects,
	}: {
		successCount: number
		failedObjects: any[]
	}) => {
		setShowResponse({
			successCount,
			failedObjects,
		})
		refreshData()
	}

	const selectedBusinessData = businesses.find(
		(b) => b._id === selectedBusiness
	)

	return (
		<Modal
			centered={true}
			closable={true}
			onCancel={handleClose}
			visible={showImportModal}
			footer={null}
			bodyStyle={{ padding: 0 }}
			width={600}
		>
			<div className="UploadModal">
				<div className="UploadTitle">
					<p>{`Upload Master Data - ${selectedBusinessData?.name || "Select Business"}`}</p>
				</div>
				{showResponse ? (
					<ImportModalResponse
						successCount={showResponse?.successCount || 0}
						failedObjects={showResponse?.failedObjects || []}
						handleClose={handleClose}
					/>
				) : (
					<ImportContent
						file={file}
						setFile={setFile}
						businessId={selectedBusiness}
						setBusinessId={setSelectedBusiness}
						businesses={businesses}
						handleShowResponse={handleShowResponse}
					/>
				)}
			</div>
		</Modal>
	)
}

const ImportContent: React.FC<{
	file: any
	setFile: (file: any) => void
	businessId: string
	setBusinessId: (id: string) => void
	businesses: any[]
	handleShowResponse: (response: any) => void
}> = ({
	file,
	setFile,
	businessId,
	setBusinessId,
	businesses,
	handleShowResponse,
}) => {
	const dispatch = useDispatch()

	const fileSize = file ? humanFileSize(file.size, true) : "0 B"

	return (
		<>
			<div className="UploadBody">
				<div style={{ marginBottom: 16 }}>
					<label
						style={{ display: "block", marginBottom: 8, fontWeight: "bold" }}
					>
						Select Business:
					</label>
					<Select
						style={{ width: "100%" }}
						placeholder="Select Business"
						value={businessId || undefined}
						onChange={setBusinessId}
						showSearch
						optionFilterProp="children"
						filterOption={(input, option) =>
							(option?.children as string)
								?.toLowerCase()
								.includes(input.toLowerCase())
						}
					>
						{businesses.map((business) => (
							<Select.Option key={business._id} value={business._id}>
								{business.name}
							</Select.Option>
						))}
					</Select>
				</div>

				<Dropzone
					className="UploadDropzone"
					id="upload_file"
					multiple={false}
					accept=".csv, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
					onDrop={(files: any) => setFile(files[0])}
				>
					{file ? (
						<React.Fragment>
							<Icons.UploadFile />
							<p>Name: {file?.name}</p>
							<p>Size: {fileSize}</p>
						</React.Fragment>
					) : (
						<React.Fragment>
							<Icons.UploadFile />
							<p>{Strings.orders.uploadFile}</p>
						</React.Fragment>
					)}
				</Dropzone>
			</div>
			<div className="UploadFooter" style={{ justifyContent: "flex-end" }}>
				<Button
					type="primary"
					size="large"
					onClick={() => {
						fileImportMasterData(dispatch, businessId, file, handleShowResponse)
					}}
					disabled={!file || !businessId}
				>
					{Strings.orders.import}
				</Button>
			</div>
		</>
	)
}

const ImportModalResponse: React.FC<{
	successCount: number
	failedObjects: any[]
	handleClose: () => void
}> = ({ successCount, failedObjects, handleClose }) => {
	// Group failed users by error message
	const groupedByError: { [error: string]: any[] } = failedObjects.reduce(
		(acc: any, user: any) => {
			const err = user.error || "unknown"
			if (!acc[err]) acc[err] = []
			acc[err].push(user)
			return acc
		},
		{}
	)

	return (
		<>
			<div className="UploadBody">
				<div className="import-summary">
					<h2>Import Completed</h2>

					<div className="summary-items">
						<div className="summary-item success">
							<span className="count">{successCount}</span>
							<span className="label">Users imported successfully</span>
						</div>

						<div className="summary-item failure">
							<span className="count">{failedObjects.length}</span>
							<span className="label">Users with errors</span>
						</div>
					</div>

					{Object.entries(groupedByError).length > 0 && (
						<div className="error-details">
							<h3>Error Details:</h3>
							{Object.entries(groupedByError).map(([error, users]) => (
								<div key={error} className="error-group">
									<h4>
										{translateError(error)} ({users.length} users)
									</h4>
									<ul>
										{users.slice(0, 5).map((user, index) => (
											<li key={index}>
												{user.name || user.clientName || user.id || "Unknown"}
											</li>
										))}
										{users.length > 5 && (
											<li>... and {users.length - 5} more</li>
										)}
									</ul>
								</div>
							))}
						</div>
					)}
				</div>
			</div>
			<div className="UploadFooter" style={{ justifyContent: "flex-end" }}>
				<Button type="primary" onClick={handleClose} size="large">
					{Strings.generic.close}
				</Button>
			</div>
		</>
	)
}

async function fileImportMasterData(
	dispatch: any,
	businessId: string,
	file: any,
	handleShowResponse: any = () => {}
) {
	const data = new FormData()
	data.append("file", file)
	data.append("business", businessId)

	dispatch(setLoader(true))

	let response: any
	try {
		response = await API.post({
			url: Endpoints.uriMasterData(`${businessId}/import`),
			data,
			axiosConfig: {
				timeout: 0,
			},
		})

		if (response.ok) {
			handleShowResponse({
				successCount: response.data.results?.successCount || 0,
				failedObjects: response.data.results?.failedUsers || [],
			})
		} else {
			notification.error({
				message: "Master Data",
				description: response?.data?.message || Strings.serverErrors.wentWrong,
				placement: "bottomRight",
				duration: 5,
			})
		}
	} catch (err) {
		notification.error({
			message: "Master Data",
			description: response?.data?.message || Strings.serverErrors.wentWrong,
			placement: "bottomRight",
			duration: 5,
		})
	} finally {
		dispatch(setLoader(false))
	}
}

function translateError(error: string) {
	switch (error) {
		case "invalid_address":
			return "Invalid Address"
		case "missing_fields":
			return "Missing Required Fields"
		case "duplicate_user":
			return "Duplicate User"
		case "invalid_coordinates":
			return "Invalid Coordinates"
		case "address_validation_required":
			return "Address Validation Required"
		default:
			return error
	}
}

export default ImportModal
